/**
 * CSV export requests (F18).
 *
 * Each function fetches the CSV for a filtered list through the authenticated
 * client and hands back the blob and its filename. Triggering the browser save
 * is separate ({@link saveCsv}) so the fetch can be tested without a DOM, and
 * so a caller can compose the two without a hidden side effect on every call.
 *
 * The query is the SAME filter object the list page already holds, so "export
 * this filtered list" (PRD F18) is exactly that — no second source of truth for
 * what the user is looking at.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.4, PRD F18
 */
import { apiDownload } from './api-client';

/** The five lists that can be exported. */
export type ExportEntity =
  | 'startups'
  | 'pilots'
  | 'customers'
  | 'assets'
  | 'contacts';

/** A downloaded CSV and the name it should save as. */
export interface DownloadedCsv {
  readonly blob: Blob;
  readonly filename: string;
}

/** The filter values an export accepts — the same shape a list query sends. */
export type ExportQuery = Record<string, string | number | boolean | null | undefined>;

/** Fetches the CSV export for one list, honouring the given filters. */
export async function fetchCsv(
  entity: ExportEntity,
  query: ExportQuery,
): Promise<DownloadedCsv> {
  const blob = await apiDownload(`/export/${entity}`, { method: 'GET', query });
  return { blob, filename: `${entity}.csv` };
}

/**
 * Triggers a browser download of a fetched CSV.
 *
 * Uses an object URL and a synthetic anchor click — the standard way to save a
 * blob the page already holds. The URL is revoked afterwards so the blob can be
 * garbage-collected rather than pinned for the life of the document.
 */
export function saveCsv({ blob, filename }: DownloadedCsv): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
